import { useConversationMessageCacheHandler } from "#features/conversations/hooks/useConversationMessageCacheHandler.ts";
import type { ConversationMessageModel } from "#features/conversations/types/ConversationMessageModel.ts";
import { useTracking } from "#hooks/useTracking.ts";
import type { PageActionButtonProps } from "#types/PageActionModel.ts";
import { MixpanelEvent, MixpanelPageActionType } from "#types/tracking.ts";
import { Button, ButtonGroup } from "@hopper-ui/components";
import { useCallback, useMemo } from "react";

interface Props {
    conversationId: string;
    message: ConversationMessageModel;
}

export const AssistantMessagePageActionButtons = ({ conversationId, message }: Props) => {
    const { updateMessage } = useConversationMessageCacheHandler();
    const { trackEvent } = useTracking();
    const executedPageAction = useMemo(() => message.pageActions?.find(x => x.action === message?.pageActionResponse?.action), [message]);

    const handlePress = useCallback(({ label, onClick, variant }: PageActionButtonProps) => {
        onClick(message.pageActionResponse!);

        trackEvent(MixpanelEvent.AssistantPageActionTriggered, {
            "Label": label,
            "Page Action": executedPageAction?.action,
            "Feature": message?.promptFeature,
            // Most of the time, the "ai" variant is used to apply the action, we consider the other variants as dismissals
            "Type": variant === "ai" ? MixpanelPageActionType.Apply : MixpanelPageActionType.Dismiss
        });

        updateMessage(conversationId, message.id, _message => ({
            ..._message,
            pageActionResponse: null
        }));
    }, [message.pageActionResponse, message?.promptFeature, message.id, trackEvent, executedPageAction?.action, updateMessage, conversationId]);

    if (!message.pageActionResponse || !executedPageAction?.buttons) {
        return null;
    }

    return (
        <ButtonGroup rowGap="inline-sm" columnGap="inline-sm">
            {executedPageAction?.buttons.map(button => {
                const { label, variant } = button;

                if (variant === "ai") {
                    return (
                        <Button
                            key={label}
                            variant="primary"
                            UNSAFE_border="1px solid var(--hop-orchid-bloom-600)"
                            backgroundImage="linear-gradient(93deg, #F6F5FF 0%, #FFF 50%, #F6F5FF 100%)"
                            color="core_orchid-bloom-600"
                            colorHover="core_orchid-bloom-800"
                            colorFocus="core_orchid-bloom-800"
                            backgroundColorHover="core_orchid-bloom-600"
                            size="sm"
                            onPress={() => handlePress(button)}
                        >
                            {label}
                        </Button>
                    );
                }

                return (
                    <Button
                        key={label}
                        variant={variant}
                        size="sm"
                        onPress={() => handlePress(button)}
                    >
                        {label}
                    </Button>
                );
            })}
        </ButtonGroup>
    );
};
